import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Typography,
  TextField,
  Button,
  Modal,
  Backdrop,
  Fade,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import GetAppIcon from "@mui/icons-material/GetApp";
import AddIcon from "@mui/icons-material/Add";
import MoneyIcon from "@mui/icons-material/Money";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Swal from "sweetalert2";

import moment from "moment";

import DataTable from "react-data-table-component";
import Toast from "../../utils/Toast";

import useGetRoomTypes from "../../queries/roomType/getRoomTypeLinks";
import useGetAgents from "../../queries/agent/getAgents";
import useDeleteAgent from "../../mutation/agent/deleteAgent";
import useDownloadAgent from "../../mutation/agent/downloadReport";
import useDownloadAccural from "../../mutation/agent/downloadAccural";
import useCreateCommission from "../../mutation/commission/createCommission";
import useCreateAudit from "../../mutation/audit/createAudit";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AuthContext } from "../../utils/userProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Select from "react-select";

import Loader from "../../components/Loader";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
  modalPaperSx,
  modalHeaderSx,
} from "../../components/ListPage";

const schema = yup.object().shape({});

function useOnMount(handler) {
  return React.useEffect(handler, []);
}

function required(displayName) {
  return function validateRequired(value) {
    return value !== null || `${displayName} is required.`;
  };
}

const Agents = () => {
  const [agentTable, setAgentTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);
  const [openDeleteAgent, setOpenDeleteAgent] = useState(false);
  const [openDownloadReport, setOpenDownloadReport] = useState(false);
  const [openCommission, setOpenCommission] = useState(false);
  const [openAccuralReport, setOpenAccuralReport] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [actionMenu, setActionMenu] = useState(null);
  const [downloadRow, setDownloadRow] = useState();
  const [commissionRow, setCommissionRow] = useState({});

  const [roomTypeOptions, setRoomTypeOptions] = useState([]);

  const [applicationDate, setApplicationDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [commissionDate, setCommissionDate] = useState(moment());
  const [accuralYear, setAccuralYear] = useState(moment());

  const {
    control,
    register,
    handleSubmit,
    setValue,
    errors,
    getValues,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useOnMount(() => {
    register({ name: "startDate" }, {});
    register({ name: "endDate" }, {});
    register({ name: "applicationDate" }, {});
    setValue("commissionDate", commissionDate);
  });

  const handleApplicationDate = (date) => {
    console.log(date);
    if (date) {
      setApplicationDate(date);
      setValue("applicationDate", date);
    } else {
      setApplicationDate(null);
      setValue("applicationDate", null);
    }
  };

  const handleStartDate = (date) => {
    if (date) {
      setStartDate(date.startOf("day"));
      setValue("startDate", date.startOf("day"));
    } else {
      setStartDate(null);
      setValue("startDate", null);
    }
  };

  const handleEndDate = (date) => {
    if (date) {
      setEndDate(date.endOf("day"));
      setValue("endDate", date.endOf("day"));
    } else {
      setEndDate(null);
      setValue("endDate", null);
    }
  };

  let columns = [
    {
      name: "Agency Name",
      selector: "agency_name",
      sortable: true,
    },
    {
      name: "Agent Code",
      selector: "agent_code",
      sortable: true,
    },
    {
      name: "Archived",
      selector: "archived",
      sortable: true,
      cell: (row, index, column, id) => {
        let text = "No";
        if (row.archived) {
          text = "Yes";
        }
        return <span>{text}</span>;
      },
      grow: 1,
    },
    {
      name: "Options",
      grow: 0,
      minWidth: "240px",
      cell: (row, index, column, id) => {
        if (!row.archived) {
          return (
            <div>
              {authState.editAccess && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<CreateIcon />}
                    onClick={() => {
                      editItem(row);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      deleteItem(row);
                    }}
                  >
                    Delete
                  </Button>
                </>
              )}
              {!authState.editAccess && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    editItem(row);
                  }}
                >
                  View
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect();
                  setActionMenu({
                    row,
                    top: rect.bottom,
                    left: rect.right,
                  });
                }}
              >
                More
              </Button>
            </div>
          );
        } else {
          return null;
        }
      },
    },
  ];

  const [agentsResponse, resetAgents] = useGetAgents();
  const [auditResponse, setAuditData] = useCreateAudit();
  const [downloadResponse, setDownloadData] = useDownloadAgent();
  const [downloadAccuralResponse, setDownloadAccuralData] =
    useDownloadAccural();
  const [roomTypesResponse, resetRoomTypes] = useGetRoomTypes();
  const [commissionAuditResponse, createCommission] = useCreateAudit();

  const [filterText, setFilterText] = useState("");

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = agentTable.filter(
    (item) =>
      item.agency_name &&
      item.agency_name.toLowerCase().includes(filterText.toLowerCase())
  );

  const updateTableData = (data) => {
    let newTableData = [...agentTable];
    if (data != null) {
      let index = newTableData.findIndex((object) => object.id == data.id);
      newTableData[index] = data;
      setAgentTable(newTableData);
    } else {
      setAgentTable(newTableData);
    }
  };

  const roomTypesChange = (values) => {
    setValue("room_types", values);
  };

  useEffect(() => {
    if (agentsResponse.data) {
      setAgentTable([...agentsResponse.data]);
    }
  }, [agentsResponse]);

  useEffect(() => {
    if (roomTypesResponse.data) {
      let data = roomTypesResponse.data;

      console.log(data);

      let roomTypeOptions = [];
      for (var i = 0; i < data.length; i++) {
        roomTypeOptions.push({
          label: data[i].name,
          value: data[i].id,
        });
      }
      setRoomTypeOptions(roomTypeOptions);
    }
  }, [roomTypesResponse]);

  useEffect(() => {
    if (commissionAuditResponse.data) {
      let data = commissionAuditResponse.data;

      if (data.error)
        return Swal.fire({
          icon: "error",
          title: "Error",
          text: "There was an error creating the commission, please try again.",
        });
      else {
        setOpenCommission(false);
        Toast.fire({
          icon: "success",
          title: "Commission Audit created",
        });
      }
    }
  }, [commissionAuditResponse]);

  const editItem = (row) => {
    if (authState.editAccess) {
      window.location.href = "/dashboard/agent/edit/" + row.id;
    } else {
      window.location.href = "/dashboard/agent/view/" + row.id;
    }
  };

  const deleteItem = (row) => {
    setDeleteRow(row);
    setOpenDeleteAgent(true);
  };

  const downloadReport = (row) => {
    setDownloadRow(row);
    setOpenDownloadReport(true);
  };

  const downloadAccuralReport = (row) => {
    setDownloadRow(row);
    setOpenAccuralReport(true);
  };

  const displayCommissionForm = (row) => {
    setCommissionRow(row);
    setValue("agent_code", row.agent_code);
    setOpenCommission(true);
  };

  const handleDownloadClose = () => {
    setApplicationDate(null);
    setStartDate(null);
    setEndDate(null);
    setValue("applicationDate", null);
    setValue("startDate", null);
    setValue("endDate", null);
    setOpenDownloadReport(false);
  };

  const handleClose = () => {
    setOpenDeleteAgent(false);
    setOpenCommission(false);
    setOpenAccuralReport(false);
  };

  const handleAccuralClose = () => {
    setAccuralYear(null);
    setOpenAccuralReport(false);
  };

  const confirmDelete = () => {
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          snapshotCurrent: deleteRow,
          currentData: deleteRow.id,
          type: "agent",
          action: "delete",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
        console.error(result.value);
      }
    });
  };

  useEffect(() => {
    if (downloadRow) {
      console.log(downloadRow);
    }
  }, [downloadRow]);

  const confirmDownload = (values) => {
    let id = downloadRow.id;
    let filter = {};

    console.log(values);

    if (values.room_types) {
      let roomTypeArray = [];
      for (var i = 0; i < values.room_types.length; i++) {
        roomTypeArray.push(values.room_types[i].value);
      }
      filter.room_types = roomTypeArray;
    }

    if (
      values.startDate &&
      values.endDate &&
      values.endDate.isBefore(values.startDate)
    ) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "End Date cannot be earlier than Start Date",
      });
      return;
    }
    if (values.startDate) {
      filter.start_date = values.startDate.toDate();
    }

    if (values.applicationDate) {
      filter.application_date = values.applicationDate.toDate();
    }

    if (values.endDate) {
      filter.end_date = values.endDate.toDate();
    }

    if (values.promo_code != "") {
      filter.promo_code = values.promo_code;
    }

    if (values.first_name != "") {
      filter.first_name = values.first_name;
    }

    if (values.last_name != "") {
      filter.last_name = values.last_name;
    }

    console.log(filter);

    setDownloadData({
      id,
      filter: filter,
      agency_name: downloadRow.agency_name,
    });
  };

  const confirmAccuralDownload = (values) => {
    let id = downloadRow.id;
    setDownloadAccuralData({
      id,
      year: accuralYear,
      agency_name: downloadRow.agency_name,
    });
  };

  useEffect(() => {
    if (downloadResponse.data) {
      if (openDownloadReport) {
        handleDownloadClose();
      }
    } else if (downloadResponse.error) {
      console.log(downloadResponse);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: downloadResponse.errorMessage.message,
      });
    }
  }, [downloadResponse]);

  useEffect(() => {
    if (downloadAccuralResponse.data) {
      if (openAccuralReport) {
        handleAccuralClose();
      }
    } else if (downloadAccuralResponse.error) {
      console.log(downloadAccuralResponse);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: downloadAccuralResponse.errorMessage.message,
      });
    }
  }, [downloadAccuralResponse]);

  useEffect(() => {
    if (auditResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Agent delete Audit Created",
      });
      setDeleteRow(null);
      setOpenDeleteAgent(false);
      resetAgents();
    } else if (auditResponse.error) {
      Toast.fire({
        icon: "error",
        text: "An Error has occured. Please try again.",
      });
      setDeleteRow(null);
      setOpenDeleteAgent(false);
    }
  }, [auditResponse]);

  const newAgent = () => {
    window.location.href = "/dashboard/agent/new";
  };

  const confirmCreateCommission = async (agentId, amount) => {
    try {
      let dataObj = {
        agent: agentId,
        amount: parseFloat(amount),
        date: commissionDate,
      };
      Swal.fire({
        title: "Create Updates",
        html: `<h4>Please provide a description before submitting your changes</h4>
        <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
        confirmButtonText: "Confirm",
        focusConfirm: false,
        preConfirm: () => {
          const actionDescription =
            Swal.getPopup().querySelector("#actionDescription").value;
          if (!actionDescription) {
            Swal.showValidationMessage(`Please enter description`);
          }
          return {
            actionDescription: actionDescription,
            newData: dataObj,
            type: "commission",
            action: "create",
            agent: agentId,
          };
        },
      }).then((result) => {
        if (result.isConfirmed) {
          createCommission(result.value);
        }
      });
    } catch (err) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: "There was an error creating the commission, please try again.",
      });
    }
  };

  const onSubmit = (values) => {
    let amount = getValues("amount");
    let agentId = agentsResponse.data.find(
      (e) => e.agent_code === getValues("agent_code")
    );

    let error = null;
    if (amount == "") error = "Amount cannot be empty!";
    if (!agentId || !agentId._id)
      error = "Missing Agent ID, please reselect an Agent.";

    if (error) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: error,
      });
    }

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, create commission!",
    }).then((result) => {
      if (result.isConfirmed) {
        confirmCreateCommission(agentId._id, amount);
      }
    });
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div>
        {authState.role == "Executive" && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={newAgent}
          >
            New Agent
          </Button>
        )}
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText]);

  return (
    <ListPageShell>
      <ListPageCard>
        {(agentsResponse.loading || roomTypesResponse.loading) && (
          <Loader />
        )}
        {!agentsResponse.loading && !roomTypesResponse.loading && (
          <div>
            <ListPageHeader title="Agents" />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 30]}
              noHeader={true}
              subHeader
              data={filteredItems}
              columns={columns}
              subHeaderComponent={subHeaderComponentMemo}
              customStyles={dataTableCustomStyles}
            />
            <Menu
              anchorReference="anchorPosition"
              anchorPosition={
                actionMenu
                  ? { top: actionMenu.top, left: actionMenu.left }
                  : undefined
              }
              open={Boolean(actionMenu)}
              onClose={() => setActionMenu(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  downloadReport(actionMenu.row);
                  setActionMenu(null);
                }}
              >
                <ListItemIcon>
                  <GetAppIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Download Report</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  downloadAccuralReport(actionMenu.row);
                  setActionMenu(null);
                }}
              >
                <ListItemIcon>
                  <GetAppIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Download Accural Report</ListItemText>
              </MenuItem>
              {authState.editAccess && (
                <MenuItem
                  onClick={() => {
                    displayCommissionForm(actionMenu.row);
                    setActionMenu(null);
                  }}
                >
                  <ListItemIcon>
                    <MoneyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Pay-out</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </div>
        )}
      </ListPageCard>
      <Modal
        aria-labelledby="delete-modal"
        aria-describedby="delete-modal-description"
        sx={modalSx}
        open={openDeleteAgent}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && auditResponse.loading) return;
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus={true}
      >
        <Fade in={openDeleteAgent}>
          <Box>
            <DeleteModalFrame title="Delete Agent">
              {deleteRow && (
                <div>
                  <p id="delete-modal-description">
                    Are you sure you want to delete{" "}
                    <b>
                      <u>{deleteRow.agency_name}</u>
                    </b>
                    ?
                  </p>
                  <p>
                    This action cannot be{" "}
                    <b>
                      <u>reversed</u>
                    </b>
                    .
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={confirmDelete}
                      variant="contained"
                      color="primary"
                      disabled={auditResponse.loading}
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="secondary"
                      disabled={auditResponse.loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </div>
              )}
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
      <Modal
        aria-labelledby="download-modal"
        aria-describedby="download-modal-description"
        sx={modalSx}
        open={openDownloadReport}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && downloadResponse.loading) return;
          handleDownloadClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={openDownloadReport}>
          <Box sx={modalPaperSx}>
            <Box sx={modalHeaderSx}>
              <Typography align="center" sx={{ color: "#fff", fontWeight: 700 }}>
                Download Agent
              </Typography>
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
              {downloadRow && (
                <div>
                  <p id="delete-modal-description">
                    Select the filters to download the Commission Report for{" "}
                    <b>
                      <u>{downloadRow.agency_name}</u>
                    </b>
                  </p>
                  <form
                    noValidate
                    onSubmit={(event) => {
                      // see here
                      event.stopPropagation();
                      handleSubmit(confirmDownload)(event);
                    }}
                  >
                    <Box sx={{ mb: 0.625 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: "bold" }}>
                        Room Types
                      </Typography>
                      <Controller
                        name="room_types"
                        control={control}
                        render={({ ...props }) => {
                          return (
                            <Select
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: errors.city
                                    ? "red"
                                    : provided.borderColor,
                                }),
                                menu: (provided, state) => ({
                                  ...provided,
                                  zIndex: 100,
                                }),
                              }}
                              isMulti
                              options={roomTypeOptions}
                              onChange={roomTypesChange}

                              /* disabled={createCommissionResponse.loading || editCommissionResponse.loading} */
                            />
                          );
                        }}
                      />
                      {errors.room_types && (
                        <Box
                          sx={{
                            mx: 1.75,
                            mt: 0.375,
                            fontFamily: "Roboto",
                            fontSize: "0.75rem",
                            fontWeight: 400,
                            color: "secondary.main",
                          }}
                        >
                          {errors.room_types.message}
                        </Box>
                      )}
                    </Box>
                    <DatePicker
                      label="Application Date"
                      value={applicationDate ? moment(applicationDate) : null}
                      onChange={handleApplicationDate}
                      format="DD/MM/YYYY"
                      disabled={downloadResponse.loading}
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          fullWidth: true,
                          margin: "normal",
                          id: "date-picker-start-date",
                          error: !!errors.applicationDate,
                          helperText:
                            errors.applicationDate &&
                            errors.applicationDate.message,
                        },
                      }}
                    />
                    <DatePicker
                      label="Start Date"
                      value={startDate ? moment(startDate) : null}
                      onChange={handleStartDate}
                      format="DD/MM/YYYY"
                      disabled={downloadResponse.loading}
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          fullWidth: true,
                          margin: "normal",
                          id: "date-picker-start-date",
                          error: !!errors.startDate,
                          helperText:
                            errors.startDate && errors.startDate.message,
                        },
                      }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate ? moment(endDate) : null}
                      onChange={handleEndDate}
                      format="DD/MM/YYYY"
                      disabled={downloadResponse.loading}
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          fullWidth: true,
                          margin: "normal",
                          id: "date-picker-end-date",
                          error: !!errors.endDate,
                          helperText:
                            errors.endDate && errors.endDate.message,
                        },
                      }}
                    />

                    <TextField
                      name="promo_code"
                      margin="normal"
                      fullWidth
                      label="Promo Code"
                      variant="outlined"
                      inputRef={register}
                      error={errors.promo_code != null}
                      helperText={
                        errors.promo_code != null
                          ? errors.promo_code.message
                          : null
                      }
                      disabled={downloadResponse.loading}
                    />

                    <TextField
                      name="first_name"
                      margin="normal"
                      fullWidth
                      label="Agent First Name"
                      variant="outlined"
                      inputRef={register}
                      error={errors.first_name != null}
                      helperText={
                        errors.first_name != null
                          ? errors.first_name.message
                          : null
                      }
                      disabled={downloadResponse.loading}
                    />

                    <TextField
                      name="last_name"
                      margin="normal"
                      fullWidth
                      label="Agent Last Name"
                      variant="outlined"
                      inputRef={register}
                      error={errors.last_name != null}
                      helperText={
                        errors.last_name != null
                          ? errors.last_name.message
                          : null
                      }
                      disabled={downloadResponse.loading}
                    />

                    <Box
                      sx={{
                        mt: 2.5,
                        mb: 2.5,
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={downloadResponse.loading}
                      >
                        Confirm
                      </Button>
                      <Button
                        onClick={handleDownloadClose}
                        variant="contained"
                        color="secondary"
                        disabled={downloadResponse.loading}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </form>
                </div>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Modal
        aria-labelledby="edit-modal"
        aria-describedby="edit-modal-description"
        sx={modalSx}
        open={openCommission}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus
      >
        <Fade in={openCommission}>
          <Box>
            <DeleteModalFrame title="Create Commission">
              <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  name="agent_code"
                  margin="normal"
                  fullWidth
                  label="Agent Code"
                  variant="outlined"
                  inputRef={register}
                  defaultValue={
                    commissionRow
                      ? commissionRow.agent_code
                      : "NO AGENT SELECTED"
                  }
                  onChange={(e) => {
                    setValue("agent_code", e.target.value);
                  }}
                  error={errors.agent_code != null}
                  helperText={
                    errors.agent_name != null
                      ? errors.agent_code.message
                      : null
                  }
                  disabled
                />
                <TextField
                  name="amount"
                  margin="normal"
                  fullWidth
                  label="Amount (AUD)*"
                  variant="outlined"
                  type="number"
                  inputRef={register}
                  onChange={(e) => {
                    setValue("amount", parseFloat(e.target.value));
                  }}
                  error={errors.amount != null}
                  helperText={
                    errors.amount != null ? errors.amount.message : null
                  }
                />
                <DatePicker
                  label="Commission Date"
                  value={commissionDate ? moment(commissionDate) : moment()}
                  onChange={(e) => {
                    setCommissionDate(e ? e.toDate() : null);
                  }}
                  format="DD/MM/YYYY"
                  disabled={downloadResponse.loading}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      margin: "normal",
                      name: "commissionDate",
                      id: "date-picker-start-date",
                      error: !!errors.commissionDate,
                      helperText:
                        errors.commissionDate &&
                        errors.commissionDate.message,
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 5,
                    gap: 1,
                  }}
                >
                  <Button type="submit" variant="contained" color="primary">
                    Confirm
                  </Button>
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                </Box>
              </form>
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
      <Modal
        aria-labelledby="accural-report-modal"
        aria-describedby="accural-report-modal-description"
        sx={modalSx}
        open={openAccuralReport}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={openAccuralReport}>
          <Box>
            <DeleteModalFrame title="Download Accural Report">
              <form
                noValidate
                onSubmit={(event) => {
                  // see here
                  event.stopPropagation();
                  handleSubmit(confirmAccuralDownload)(event);
                }}
              >
                <DatePicker
                  label="Commission Date"
                  views={["year"]}
                  value={accuralYear ? moment(accuralYear) : moment()}
                  onChange={(e) => {
                    setAccuralYear(e ? e.toDate() : null);
                  }}
                  format="YYYY"
                  disabled={downloadAccuralResponse.loading}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      margin: "normal",
                      name: "accuralYear",
                      id: "date-picker-start-date",
                      error: !!errors.accuralYear,
                      helperText:
                        errors.accuralYear && errors.accuralYear.message,
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 5,
                    gap: 1,
                  }}
                >
                  <Button type="submit" variant="contained" color="primary">
                    Confirm
                  </Button>
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                </Box>
              </form>
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default Agents;
